import { useState, ReactNode } from "react";

interface SpaButtonProps {
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  variant?: "primary" | "outline" | "ghost";
  children: ReactNode;
  className?: string;
  target?: string;
  rel?: string;
  "aria-label"?: string;
}

/**
 * 共通ボタンコンポーネント
 * ホバー時：背景色が下からスライドアップして塗りつぶされる
 */
export default function SpaButton({
  href,
  onClick,
  variant = "primary",
  children,
  className = "",
  target,
  rel,
  "aria-label": ariaLabel,
}: SpaButtonProps) {
  const [hovered, setHovered] = useState(false);

  const baseStyle: React.CSSProperties = {
    fontFamily: "'Noto Serif JP', serif",
    position: "relative",
    overflow: "hidden",
    alignItems: "center",
    gap: "10px",
    padding: "12px 28px",
    fontSize: "0.8rem",
    letterSpacing: "0.15em",
    fontWeight: 300,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "color 0.35s ease",
    textDecoration: "none",
  };

  const variantStyle: React.CSSProperties =
    variant === "primary"
      ? {
          background: "#8B7355",
          color: hovered ? "#8B7355" : "#FAFAF8",
          border: "1px solid #8B7355",
        }
      : variant === "outline"
      ? {
          background: "transparent",
          color: hovered ? "#2C2C2C" : "#FAFAF8",
          border: "1px solid rgba(250,250,248,0.45)",
        }
      : {
          background: "transparent",
          color: hovered ? "#FAFAF8" : "#8B7355",
          border: "1px solid #8B7355",
        };

  const fillColor =
    variant === "primary"
      ? "#FAFAF8"
      : variant === "outline"
      ? "#FAFAF8"
      : "#8B7355";

  // className に w-full が含まれるかで display を切り替え
  const isBlock = className.includes("w-full");
  const displayStyle: React.CSSProperties = {
    display: isBlock ? "flex" : "inline-flex",
  };

  const sharedProps = {
    style: { ...baseStyle, ...displayStyle, ...variantStyle },
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
    "aria-label": ariaLabel,
    className,
  };

  const inner = (
    <>
      {/* Slide-up fill layer */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: fillColor,
          transform: hovered ? "translateY(0%)" : "translateY(101%)",
          transition: "transform 0.38s cubic-bezier(0.77, 0, 0.175, 1)",
          zIndex: 0,
        }}
      />
      {/* Content */}
      <span style={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: "10px" }}>
        {children}
      </span>
    </>
  );

  if (href) {
    return (
      <a href={href} onClick={onClick} target={target} rel={rel} {...sharedProps}>
        {inner}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} {...sharedProps}>
      {inner}
    </button>
  );
}
